import React, { useState, useEffect } from "react";
import {
  BarChart2,
  TrendingUp,
  TrendingDown,
  Zap,
  BookOpen,
  Target,
  CheckCircle,
  AlertCircle,
  Clock,
  Calendar,
  Award,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  PieChart,
} from "lucide-react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { getUserTrades } from "../../utils/firebaseTradeUtils";
import { useAuth } from "../../context/AuthContext";
import "./developmentHub.css";

// 1. Import the new utility functions at the top
import {
  calculateSkillsAssessment,
  generateTradingInsights,
  generateRecommendedActions,
} from "../../utils/skillsAssessment";

const DevelopmentHub = () => {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("all");
  const [expandedInsights, setExpandedInsights] = useState([0]);
  const [skillsData, setSkillsData] = useState(null);
  const [progressData, setProgressData] = useState([]);
  const [expandedSection, setExpandedSection] = useState("overview");

  useEffect(() => {
    const fetchTrades = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const fetchedTrades = await getUserTrades(user.uid);
        setTrades(fetchedTrades);

        // 2. Replace generateSkillsAssessment with shared utility function
        generateSkillsAssessment(fetchedTrades);

        // Generate progress data for last 6 months
        generateProgressData(fetchedTrades);
      } catch (error) {
        console.error("Error fetching trades:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrades();
  }, [user]);

  // 2. Updated generateSkillsAssessment function using the shared utility function
  const generateSkillsAssessment = (fetchedTrades) => {
    const skills = calculateSkillsAssessment(fetchedTrades);
    setSkillsData(skills);
  };

  const generateProgressData = (trades) => {
    // Create monthly progress data for the past 6 months
    const months = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = d.toLocaleString("default", { month: "short" });
      months.push({
        month: monthName,
        year: d.getFullYear(),
        monthIndex: d.getMonth(),
        fullYear: d.getFullYear(),
      });
    }

    const progressByMonth = months.map((monthData) => {
      const { month, monthIndex, fullYear } = monthData;

      // Filter trades for this month
      const monthTrades = trades.filter((trade) => {
        const tradeDate = new Date(trade.date);
        return (
          tradeDate.getMonth() === monthIndex &&
          tradeDate.getFullYear() === fullYear
        );
      });

      // Calculate win rate
      const winningTrades = monthTrades.filter((trade) => trade.pnl > 0);
      const winRate =
        monthTrades.length > 0
          ? (winningTrades.length / monthTrades.length) * 100
          : 0;

      // Calculate profit factor
      const totalProfit = winningTrades.reduce(
        (sum, trade) => sum + trade.pnl,
        0
      );
      const losingTrades = monthTrades.filter((trade) => trade.pnl < 0);
      const totalLoss = Math.abs(
        losingTrades.reduce((sum, trade) => sum + trade.pnl, 0)
      );
      const profitFactor =
        totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? 2 : 0;

      // Simulate skill improvement over time for demo purposes
      const skillScore = Math.min(
        95,
        55 + monthTrades.length * 7 + (Math.random() * 10 - 5)
      );

      return {
        name: month,
        winRate: Math.round(winRate),
        profitFactor: parseFloat(profitFactor.toFixed(2)),
        skillScore: Math.round(skillScore),
        tradeCount: monthTrades.length,
      };
    });

    setProgressData(progressByMonth);
  };

  // 3. Replace the getInsights function with the shared utility function
  const getInsights = () => {
    return generateTradingInsights(trades);
  };

  // 4. Replace the getRecommendedActions function with the shared utility function
  const getRecommendedActions = () => {
    return generateRecommendedActions(skillsData);
  };

  const toggleInsight = (index) => {
    if (expandedInsights.includes(index)) {
      setExpandedInsights(expandedInsights.filter((i) => i !== index));
    } else {
      setExpandedInsights([...expandedInsights, index]);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderSkillMeter = (value, label) => {
    const getColor = (val) => {
      if (val >= 80) return "#10b981"; // Green
      if (val >= 60) return "#22c55e"; // Light green
      if (val >= 40) return "#f59e0b"; // Amber
      return "#ef4444"; // Red
    };

    return (
      <div className="skill-meter">
        <div className="skill-label">{label}</div>
        <div className="skill-bar-container">
          <div
            className="skill-bar"
            style={{
              width: `${value}%`,
              backgroundColor: getColor(value),
            }}
          >
            <span className="skill-value">{value}</span>
          </div>
        </div>
      </div>
    );
  };

  // 5. Updated renderActionPriorityBadge for better clarity
  const renderActionPriorityBadge = (priority) => {
    const colors = {
      critical: "#DC2626", // Darker red for critical
      high: "#ef4444",
      medium: "#f59e0b",
      low: "#22c55e",
    };

    const labels = {
      critical: "Critical Priority",
      high: "High Priority",
      medium: "Medium Priority",
      low: "Low Priority",
    };

    return (
      <span
        className="action-priority-badge"
        style={{ backgroundColor: colors[priority] }}
      >
        {labels[priority]}
      </span>
    );
  };

  // 6. Updated renderActionDifficultyBadge for better clarity
  const renderActionDifficultyBadge = (difficulty) => {
    const colors = {
      easy: "#22c55e",
      medium: "#f59e0b",
      hard: "#ef4444",
    };

    const labels = {
      easy: "Easy to Implement",
      medium: "Moderate Effort",
      hard: "Requires Commitment",
    };

    return (
      <span
        className="action-difficulty-badge"
        style={{ backgroundColor: colors[difficulty] }}
      >
        {labels[difficulty]}
      </span>
    );
  };

  // 7. Dispatch an event with the skills data for dashboard usage
  useEffect(() => {
    if (skillsData) {
      const event = new CustomEvent("masteryScoreUpdated", {
        detail: skillsData,
      });
      window.dispatchEvent(event);
    }
  }, [skillsData]);

  if (loading) {
    return (
      <div className="development-hub-container loading">
        <div className="loading-spinner">
          <RefreshCw size={32} className="spinner" />
          <p>Analyzing your trading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="development-hub-container">
      <div className="development-hub-header">
        <div className="time-filter">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="year">Last Year</option>
            <option value="6months">Last 6 Months</option>
            <option value="3months">Last 3 Months</option>
            <option value="month">Last Month</option>
          </select>
        </div>
      </div>

      {/* Overview Section */}
      <div className="hub-section">
        <div
          className="section-header clickable"
          onClick={() => toggleSection("overview")}
        >
          <h3>
            <BarChart2 size={20} />
            Performance Overview
          </h3>
          <div className="section-toggle">
            {expandedSection === "overview" ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </div>
        </div>

        {expandedSection === "overview" && (
          <div className="section-content">
            <div className="overview-grid">
              <div className="overall-score-card">
                <div className="score-label">Trading Mastery Score</div>
                <div
                  className={`score-value ${
                    skillsData?.overallScore >= 80
                      ? "excellent"
                      : skillsData?.overallScore >= 60
                      ? "good"
                      : skillsData?.overallScore >= 40
                      ? "average"
                      : "needs-work"
                  }`}
                >
                  {skillsData?.overallScore || 0}
                </div>
                <div className="score-info">
                  {skillsData?.overallScore >= 80
                    ? "Excellent"
                    : skillsData?.overallScore >= 60
                    ? "Good"
                    : skillsData?.overallScore >= 40
                    ? "Average"
                    : "Needs Work"}
                </div>
              </div>

              <div className="progress-chart-card">
                <h4>Your Progress</h4>
                <div className="progress-chart">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={progressData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                      />
                      <Tooltip />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="profitFactor"
                        stroke="#10b981"
                        activeDot={{ r: 8 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="skillScore"
                        stroke="#3b82f6"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="insights-container">
              <h4>Key Insights</h4>
              <div className="insights-list">
                {getInsights().map((insight, index) => (
                  <div
                    key={index}
                    className={`insight-card ${insight.type}`}
                    onClick={() => toggleInsight(index)}
                  >
                    <div className="insight-header">
                      <div className="insight-icon">{insight.icon}</div>
                      <div className="insight-title">{insight.title}</div>
                      <div className="insight-expand">
                        {expandedInsights.includes(index) ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </div>
                    </div>
                    {expandedInsights.includes(index) && (
                      <div className="insight-details">
                        {insight.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Skills Assessment Section */}
      <div className="hub-section">
        <div
          className="section-header clickable"
          onClick={() => toggleSection("skills")}
        >
          <h3>
            <Zap size={20} />
            Skills Assessment
          </h3>
          <div className="section-toggle">
            {expandedSection === "skills" ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </div>
        </div>

        {expandedSection === "skills" && (
          <div className="section-content">
            <div className="skills-grid">
              <div className="skills-radar">
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart
                    outerRadius={90}
                    data={[
                      {
                        skill: "Entry Timing",
                        value: skillsData?.entryTiming || 0,
                        fullMark: 100,
                      },
                      {
                        skill: "Exit Execution",
                        value: skillsData?.exitExecution || 0,
                        fullMark: 100,
                      },
                      {
                        skill: "Risk Management",
                        value: skillsData?.riskManagement || 0,
                        fullMark: 100,
                      },
                      {
                        skill: "Emotional Control",
                        value: skillsData?.emotionalControl || 0,
                        fullMark: 100,
                      },
                      {
                        skill: "Strategy Adherence",
                        value: skillsData?.strategyAdherence || 0,
                        fullMark: 100,
                      },
                      {
                        skill: "Pattern Recognition",
                        value: skillsData?.patternRecognition || 0,
                        fullMark: 100,
                      },
                    ]}
                  >
                    <PolarGrid />
                    <PolarAngleAxis dataKey="skill" />
                    <Radar
                      name="Skills"
                      dataKey="value"
                      stroke="#10b981"
                      fill="#10b981"
                      fillOpacity={0.5}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="skills-details">
                <h4>Skill Breakdown</h4>
                <div className="skills-list">
                  {renderSkillMeter(
                    skillsData?.entryTiming || 0,
                    "Entry Timing"
                  )}
                  {renderSkillMeter(
                    skillsData?.exitExecution || 0,
                    "Exit Execution"
                  )}
                  {renderSkillMeter(
                    skillsData?.riskManagement || 0,
                    "Risk Management"
                  )}
                  {renderSkillMeter(
                    skillsData?.emotionalControl || 0,
                    "Emotional Control"
                  )}
                  {renderSkillMeter(
                    skillsData?.strategyAdherence || 0,
                    "Strategy Adherence"
                  )}
                  {renderSkillMeter(
                    skillsData?.patternRecognition || 0,
                    "Pattern Recognition"
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Plan Section */}
      <div className="hub-section">
        <div
          className="section-header clickable"
          onClick={() => toggleSection("action")}
        >
          <h3>
            <Target size={20} />
            Action Plan
          </h3>
          <div className="section-toggle">
            {expandedSection === "action" ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </div>
        </div>

        {expandedSection === "action" && (
          <div className="section-content">
            <div className="action-plan-grid">
              <div className="recommended-actions">
                <h4>Recommended Actions</h4>
                <div className="actions-list">
                  {getRecommendedActions().map((action, index) => (
                    <div key={index} className="action-card">
                      <div className="action-priority">
                        {renderActionPriorityBadge(action.priority)}
                      </div>
                      <div className="action-content">
                        <h5>{action.title}</h5>
                        <p>{action.description}</p>
                      </div>
                      <div className="action-difficulty">
                        {renderActionDifficultyBadge(action.difficulty)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="learning-resources">
                <h4>Learning Resources</h4>
                <div className="resources-list">
                  {(() => {
                    const resources = [
                      {
                        title: "Advanced Exit Strategies",
                        type: "Article",
                        source: "Trading Journal",
                        link: "#",
                      },
                      {
                        title: "The Psychology of Risk Management",
                        type: "Book",
                        source: "Recommended Reading",
                        link: "#",
                      },
                      {
                        title: "Mastering Breakout Patterns",
                        type: "Video Course",
                        source: "Trading Education",
                        link: "#",
                      },
                      {
                        title: "Position Sizing Calculator",
                        type: "Tool",
                        source: "Risk Management",
                        link: "#",
                      },
                    ];
                    return resources.map((resource, index) => (
                      <div key={index} className="resource-card">
                        <div className="resource-icon">
                          {resource.type === "Article" && (
                            <BookOpen size={20} />
                          )}
                          {resource.type === "Book" && <BookOpen size={20} />}
                          {resource.type === "Video Course" && (
                            <PieChart size={20} />
                          )}
                          {resource.type === "Tool" && <Target size={20} />}
                        </div>
                        <div className="resource-content">
                          <h5>{resource.title}</h5>
                          <p>
                            {resource.type} • {resource.source}
                          </p>
                        </div>
                        <div className="resource-action">
                          <a href={resource.link} className="resource-link">
                            View
                          </a>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevelopmentHub;
